import {
  type RolesListQuerySchema,
  type CreateRoleRequest,
  type Paginated,
  type PermissionDefinition,
  type Role,
  type RoleDetail,
  type UpdateRoleRequest,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The wire shape — `z.input`, because `ids` travels as one comma-joined value. */
type RolesParams = z.input<typeof RolesListQuerySchema>;

/**
 * Roles, as the module that OWNS them reads and writes them.
 *
 * `shared/services/role-lookup` exists alongside this and is not a duplicate:
 * that one is the read-only bridge every other module uses (W6), this one is the
 * owner's full surface. Splitting them is what lets the bridge stay importable
 * from `shared/` without dragging a module's mutations into it.
 *
 * Every mutation publishes `access.role.changed` on the bus AFTER the request
 * resolves (W5), so surfaces showing roles from another angle — the staff list's
 * role column, a future permissions preview — refresh without a reload and
 * without importing this file.
 */
export const rolesApi = createApi({
  reducerPath: 'rolesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Role', 'RoleList'],
  endpoints: (builder) => ({
    listRoles: builder.query<Paginated<Role>, RolesParams>({
      query: (params) => ({ url: '/admin/roles', method: 'GET', params }),
      providesTags: ['RoleList'],
    }),

    roleDetail: builder.query<RoleDetail, string>({
      query: (id) => ({ url: `/admin/roles/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Role' as const, id }],
    }),

    /**
     * The permission catalog, served from CODE rather than the table (the api
     * says so explicitly): the code is the source of truth and the table is its
     * seeded mirror, so the editor and the guard can never disagree about which
     * keys exist.
     */
    permissionCatalog: builder.query<readonly PermissionDefinition[], void>({
      query: () => ({ url: '/admin/roles/catalog', method: 'GET' }),
    }),

    createRole: builder.mutation<RoleDetail, CreateRoleRequest>({
      query: (body) => ({ url: '/admin/roles', method: 'POST', body }),
      invalidatesTags: ['RoleList'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          EVENT_BUS.publish({ type: 'access.role.changed', roleId: data.id });
        } catch {
          // The mutation was refused, so there is nothing to announce. Swallowed
          // deliberately: `onQueryStarted` has no caller, so a rejection escaping
          // it becomes an unhandled rejection — which is how this surfaced, as a
          // flaky test run rather than as an error anyone could act on. The
          // failure itself is already the caller's, through `unwrap()`.
        }
      },
    }),

    updateRole: builder.mutation<RoleDetail, { id: string; body: UpdateRoleRequest }>({
      query: ({ id, body }) => ({ url: `/admin/roles/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => ['RoleList', { type: 'Role' as const, id }],
      async onQueryStarted({ id }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'access.role.changed', roleId: id });
        } catch {
          // The mutation was refused, so there is nothing to announce. Swallowed
          // deliberately: `onQueryStarted` has no caller, so a rejection escaping
          // it becomes an unhandled rejection — which is how this surfaced, as a
          // flaky test run rather than as an error anyone could act on. The
          // failure itself is already the caller's, through `unwrap()`.
        }
      },
    }),

    deleteRole: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/roles/${id}`, method: 'DELETE' }),
      invalidatesTags: ['RoleList'],
      async onQueryStarted(id, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'access.role.changed', roleId: id });
        } catch {
          // The mutation was refused, so there is nothing to announce. Swallowed
          // deliberately: `onQueryStarted` has no caller, so a rejection escaping
          // it becomes an unhandled rejection — which is how this surfaced, as a
          // flaky test run rather than as an error anyone could act on. The
          // failure itself is already the caller's, through `unwrap()`.
        }
      },
    }),
  }),
});

export const {
  useListRolesQuery,
  useRoleDetailQuery,
  usePermissionCatalogQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = rolesApi;
